import { lastValueFrom } from 'rxjs';
import { Router } from '@angular/router';
import { Component } from '@angular/core';
import { LANG } from 'src/app/models/enums/lang.enum';
import { TranslateService } from '@ngx-translate/core';
import { environment } from 'src/environments/environment';
import { Pagination as Page } from 'src/app/classes/pagination';
import { GenderChar } from 'src/app/models/enums/gender-char.enum';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { NationalityService } from 'src/app/services/nationality.service';
import { RESPONSE_STATUS } from 'src/app/models/enums/response-status.enum';
import { UserRegisterService } from 'src/app/services/user-register.service';
import { CountryISO } from 'src/app/components/shares/ngx-intl-input/enums/country-iso.enum';
import { SearchCountryField } from 'src/app/components/shares/ngx-intl-input/enums/search-country-field.enum';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmDialogComponent } from 'src/app/components/shares/confirm-dialog/confirm-dialog.component';
import { CONFIRM_TYPE } from 'src/app/components/shares/confirm-dialog-button/confirm-dialog-button.component';

@Component({
  selector: 'app-register',
  templateUrl: './register.component.html',
  styleUrl: './register.component.scss',
})
export class RegisterComponent {
  lang: LANG;

  form: FormGroup;
  uniqueIndex;
  static nextId = 0;

  showPassword = false;
  showConPassword = false;

  isKhmerNationality = true;

  optionals: any = {};

  readonly LANG = LANG;
  readonly GENDER = GenderChar;
  readonly CountryISO = CountryISO;
  readonly SearchCountryField = SearchCountryField;

  version = environment.VERSION;

  nationalities = new Array<any>();
  isNationalityEmpty: boolean = false;
  getNationalityQueue: boolean = false;
  nationalitySearchString: string = '';
  nationalityPage: Page = new Page(1, 50);

  protected isValidatePhone: boolean = true;

  constructor(
    private router: Router,
    private fb: FormBuilder,
    private dialog: MatDialog,
    private translateService: TranslateService,
    private registerService: UserRegisterService,
    private nationalityService: NationalityService,
  ) {
    this.uniqueIndex = ++RegisterComponent.nextId;
    this.lang = this.translateService.currentLang as LANG;

    this.form = this.fb.group(
      {
        email: ['', [Validators.required, Validators.email]],
        username: ['', Validators.required],
        // nationality_id: ['', Validators.required],
        personal_code: ['', []],
        // passport: [''],
        // issue_date: [null, Validators.required],
        // expiry_date: [null, Validators.required],
        last_name_kh: ['', Validators.required],
        first_name_kh: ['', Validators.required],
        // last_name_en: ['', Validators.required],
        // first_name_en: ['', Validators.required],
        // gender: ['', Validators.required],
        // dob: [null, Validators.required],
        phone: [null, Validators.required],
        password: ['', Validators.required],
        con_password: ['', Validators.required],
      },
      // {
      //   validators: this.passwordMatchValidator.bind(this),
      // }
    );
  }

  ngOnInit(): void {
    this.getNationality();

    this.form.get('nationality_id')?.valueChanges.subscribe((id) => {
      const selected = this.nationalities.find(
        (nat) => nat.nationality_id === id,
      );
      this.isKhmerNationality =
        selected?.nationality_en?.toLowerCase() === 'khmer' ||
        selected?.nationality_kh?.includes('ខ្មែរ');

      if (this.isKhmerNationality) {
        this.form.get('passport')?.clearValidators();
        this.form
          .get('personal_code')
          ?.setValidators([Validators.required, this.idCardLengthValidator]);
      } else {
        this.form.get('personal_code')?.clearValidators();
        this.form.get('passport')?.setValidators([Validators.required]);
      }

      this.form.get('personal_code')?.updateValueAndValidity();
      this.form.get('passport')?.updateValueAndValidity();
    });

    this.form.get('issue_date')?.valueChanges.subscribe((issueDate) => {
      if (issueDate) {
        const issue = new Date(issueDate);
        const expiry = new Date(issue);
        expiry.setFullYear(issue.getFullYear() + 10);
        expiry.setDate(expiry.getDate() - 1);
        this.form.patchValue({ expiry_date: expiry });
      } else {
        this.form.patchValue({ expiry_date: null });
      }
    });
  }

  async getNationality(search: string = ''): Promise<void> {
    this.getNationalityQueue = true;
    const count = search ? this.nationalityPage.count : 5;
    const res = await lastValueFrom(
      this.nationalityService.getAllNationalityWithPagination(
        this.nationalityPage.page,
        count,
        search,
      ),
    );
    this.getNationalityQueue = false;
    if (res.status === RESPONSE_STATUS.SUCCESS) {
      this.nationalities.push(...res.data);
      this.isNationalityEmpty = this.nationalities.length === 0;
    }
  }

  get showPasswordMismatchError(): boolean {
    return (
      this.form.hasError('passwordMismatch') &&
      !!this.form.get('con_password')?.touched &&
      !!this.form.get('password')?.touched
    );
  }

  private passwordMatchValidator(
    control: AbstractControl,
  ): ValidationErrors | null {
    const password = control.get('password');
    const confirmPassword = control.get('con_password');

    if (!password || !confirmPassword) {
      return null;
    }

    if (password.value !== confirmPassword.value) {
      return { passwordMismatch: true };
    }

    return null;
  }

  idCardLengthValidator(control: AbstractControl): ValidationErrors | null {
    const value = control.value;
    if (value && value.length !== 9) {
      return { invalidLength: true };
    }
    return null;
  }

  onNationalitySearchChange(search: string): void {
    this.nationalitySearchString = search;
    this.nationalities = [];
    this.nationalityPage.page = 1;
    this.getNationality(search);
  }

  onSubmit(): void {
    this.form.markAllAsTouched();
    const data = this.form.value;

    const value = {
      ...data,
      phone_number: data.phone?.e164Number,
    };

    if (!this.isKhmerNationality) {
      delete value.personal_code;
    } else {
      delete value.passport;
    }

    this.registerService.save(value).subscribe((res) => {
      const username = res.data.data.email;

      if (res.status === RESPONSE_STATUS.SUCCESS) {
        const dialogRef = this.dialog.open(ConfirmDialogComponent, {
          width: '500px',
          disableClose: true,
          autoFocus: false,
          data: {
            title: 'general.msg_success',
            confirm_desc: this.translateService.instant('button.des', {
              username,
            }),
            confirm_type: CONFIRM_TYPE.NORMAL,
            is_no_action: false,
            only_confirm: true,
          },
        });

        dialogRef.afterClosed().subscribe(() => {
          this.router.navigate(['/login']);
        });
      }
    });
  }
}
